import { useEffect } from "react"
import { formatDay } from "../game/calendarEngine"

/**
 * What the world did while the player was elsewhere. Shown as a blocking
 * dispatch rather than a toast, because these are exactly the beats the
 * player needs to plan the *next* loop around — a village that burned on day
 * 12 because nobody was there is the single most important thing that
 * happened that day, and it used to be reported nowhere at all.
 */
export function DayDigest({
  day,
  reports,
  onDismiss,
}: {
  day: number
  reports: string[]
  onDismiss: () => void
}) {
  // The digest is a blocking dispatch the player reads and dismisses on the way
  // to acting on it, so Escape and Enter both close it — the same keys that
  // back out of and confirm everywhere else — rather than forcing a click.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" || event.key === "Enter") {
        event.preventDefault()
        onDismiss()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [onDismiss])

  if (reports.length === 0) return null

  return (
    <div className="digest-backdrop" role="dialog" aria-modal="true" aria-label="News from the realm">
      <div className="digest">
        <h2>News reaches you</h2>
        <p className="digest-day">{formatDay(day)}</p>
        <ul className="digest-list">
          {reports.map((report, index) => (
            // Each dispatch is penned on in turn, a beat apart, so the digest
            // reads as news arriving rather than a wall of text appearing.
            <li key={index} className="digest-line" style={{ animationDelay: `${index * 90 + 40}ms` }}>
              {report}
            </li>
          ))}
        </ul>
        <button type="button" autoFocus onClick={onDismiss}>
          Continue
        </button>
      </div>
    </div>
  )
}
